//src/pages/patientPortal/PatientPortalHeader.jsx
import React, { useState } from "react";
import { Box, Button, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import PublicIcon from "@mui/icons-material/Public";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import { useNavigate } from "react-router-dom";

const BLUE = "#0B78D0";
const BLUE_DARK = "#0866B3";
const TEXT_DARK = "#2F3A4A";
const TEXT_GREY = "#6B7280";
const BORDER = "#E5E9EF";

const PatientPortalHeader = ({
  language,
  t,
  isAuthenticated,
  onLanguageChange,
  onSignIn,
  onLogout,
  onEditProfile,
  onMyProfile,
  onSearch,
  onNavigateSection,
}) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const goToLandingSection = (section) => {
    setMobileOpen(false);
    const path = String(window.location.pathname || "").replace(/\/$/, "");
    if (path.endsWith("/landing")) {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    navigate(`/landing#${section}`);
  };

  const handleSearch = () => {
    setMobileOpen(false);
    if (onSearch) {
      onSearch();
      return;
    }
    goToLandingSection("doctor-section");
  };

  const handleNavItem = (label, section) => {
    if (label === "My Profile") {
      handleMyProfile();
      return;
    }
    if (label === "Doctors" && onSearch) {
      handleSearch();
      return;
    }
    if (onNavigateSection && section) {
      setMobileOpen(false);
      onNavigateSection(label, section);
      return;
    }
    goToLandingSection(section);
  };

  const handleMyProfile = () => {
    setMobileOpen(false);
    if (onMyProfile) {
      onMyProfile();
      return;
    }
    if (isAuthenticated) {
      navigate("/patient");
      return;
    }
    onSignIn?.();
  };

  const handleEditProfile = () => {
    setMobileOpen(false);
    onEditProfile?.();
  };

  const handleSignIn = () => {
    setMobileOpen(false);
    onSignIn?.();
  };

  const handleLogout = () => {
    setMobileOpen(false);
    onLogout?.();
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1200,
        backgroundColor: "#FFFFFF",
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 3, md: 6, lg: 10 } }}>
        <Box
          sx={{
            minHeight: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              flexShrink: 0,
            }}
            onClick={() => navigate("/landing")}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: BLUE,
                color: "white",
              }}
            >
              <LocalHospitalIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.4px" }}>
              HMS
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: { sm: 1.5, md: 2.5, lg: 3.5 },
              flex: 1,
              pl: { sm: 1, md: 4 },
            }}
          >
            {[
              ["Modules", "modules-section"],
              ["Doctors", "doctor-section"],
              ["My Profile", null],
              ["About", "about-section"],
            ].map(([label, section]) => (
              <Box
                key={label}
                onClick={() => handleNavItem(label, section)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: TEXT_DARK,
                  "&:hover": { color: BLUE },
                }}
              >
                {t(label)}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <IconButton
              onClick={handleSearch}
              aria-label={t("Find a Doctor")}
              sx={{ display: { xs: "none", sm: "flex" }, color: TEXT_DARK }}
            >
              <SearchIcon />
            </IconButton>

            <TextField
              select
              size="small"
              value={language}
              onChange={(e) => onLanguageChange(e.target.value)}
              aria-label={t("Select Language")}
              sx={{
                display: { xs: "none", sm: "flex" },
                minWidth: { sm: 118, md: 130 },
                mr: 0.5,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "20px",
                  backgroundColor: "#fff",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: BORDER,
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: 13,
                  fontWeight: 600,
                  py: 0.5,
                },
              }}
              SelectProps={{
                IconComponent: () => (
                  <PublicIcon sx={{ fontSize: 17, mr: 1, color: TEXT_GREY }} />
                ),
              }}
            >
              <MenuItem value="en">{t("English")}</MenuItem>
              <MenuItem value="hi">{t("Hindi")}</MenuItem>
              <MenuItem value="mr">{t("Marathi")}</MenuItem>
            </TextField>

            {isAuthenticated ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleEditProfile}
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    borderColor: BORDER,
                    color: BLUE,
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "24px",
                    px: 2,
                    whiteSpace: "nowrap",
                  }}
                  startIcon={<AccountCircleIcon />}
                >
                  {t("Edit Profile")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleLogout}
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    backgroundColor: BLUE,
                    color: "#fff",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "24px",
                    px: 2.4,
                    py: 0.85,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: BLUE_DARK, boxShadow: "none" },
                  }}
                >
                  {t("Logout")}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleSignIn}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  backgroundColor: BLUE,
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "24px",
                  px: 2.4,
                  py: 0.85,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: BLUE_DARK, boxShadow: "none" },
                }}
              >
                {t("Sign In")}
              </Button>
            )}

            <IconButton
              onClick={() => setMobileOpen((open) => !open)}
              sx={{ display: { xs: "flex", sm: "none" }, color: TEXT_DARK }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {mobileOpen && (
        <Box
          sx={{
            display: { sm: "none" },
            px: 2,
            pb: 2,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <TextField
            select
            fullWidth
            size="small"
            label={t("Select Language")}
            value={language}
            onChange={(e) => onLanguageChange(e.target.value)}
            sx={{ mt: 2, mb: 1.2 }}
          >
            <MenuItem value="en">{t("English")}</MenuItem>
            <MenuItem value="hi">{t("Hindi")}</MenuItem>
            <MenuItem value="mr">{t("Marathi")}</MenuItem>
          </TextField>

          {[
            ["Modules", "modules-section"],
            ["Doctors", "doctor-section"],
            ["My Profile", null],
            ["About", "about-section"],
          ].map(([label, section]) => (
            <Typography
              key={label}
              onClick={() => handleNavItem(label, section)}
              sx={{ py: 1.2, cursor: "pointer", fontSize: 15 }}
            >
              {t(label)}
            </Typography>
          ))}
          <Typography
            onClick={handleSearch}
            sx={{ py: 1.2, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", gap: 1 }}
          >
            <SearchIcon sx={{ fontSize: 18 }} />
            {t("Find a Doctor")}
          </Typography>

          {isAuthenticated ? (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleEditProfile}
                sx={{
                  color: BLUE,
                  borderColor: BORDER,
                  fontWeight: 700,
                  textTransform: "none",
                  mt: 1,
                }}
                startIcon={<AccountCircleIcon />}
              >
                {t("Edit Profile")}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleLogout}
                sx={{
                  backgroundColor: BLUE,
                  fontWeight: 700,
                  textTransform: "none",
                  mt: 1,
                  boxShadow: "none",
                }}
              >
                {t("Logout")}
              </Button>
            </>
          ) : (
            <Button
              fullWidth
              variant="contained"
              onClick={handleSignIn}
              sx={{
                backgroundColor: BLUE,
                fontWeight: 700,
                textTransform: "none",
                mt: 1,
                boxShadow: "none",
              }}
            >
              {t("Sign In")}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PatientPortalHeader;
